(() => {
  const maxAge = 10 * 60 * 1000;
  const captchaWait = 30 * 1000;
  let optInResultOnPage = false;

  function storedAttempt(key) {
    try {
      const value = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (value && typeof value.at === 'number' && typeof value.email === 'string' &&
        value.at <= Date.now() && Date.now() - value.at < maxAge) return value;
      sessionStorage.removeItem(key);
    } catch {}
    return null;
  }

  function saveAttempt(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
    window.setTimeout(() => {
      if (storedAttempt(key)?.at === value.at) clearAttempt(key);
    }, Math.max(0, value.at + maxAge - Date.now()) + 1);
  }

  function clearAttempt(key) {
    try { sessionStorage.removeItem(key); } catch {}
  }

  function clarityAllowed() {
    const gate = document.querySelector('script[src*="third-party.js"][data-require-consent]');
    return gate?.dataset.requireConsent !== 'true' ||
      Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
  }

  // With analytics consent required, third-party.js defines Clarity only after
  // the consent API answers, so reports raised earlier wait for its event.
  const pendingClarity = [];

  function sendToClarity({ event, formId, detail, email }) {
    if (typeof window.clarity !== 'function' || !clarityAllowed()) return false;
    try {
      if (email) Promise.resolve(window.clarity('identify', email.trim().toLowerCase())).catch(() => {});
      window.clarity('set', 'interspire_signup_form', formId);
      window.clarity('set', 'interspire_signup_outcome', detail.outcome || event);
      if (detail.type) window.clarity('set', 'interspire_signup_failure_type', detail.type);
      window.clarity('event', event);
      window.clarity('upgrade', 'interspire_signup_failure');
    } catch {}
    return true;
  }

  function deliver(entry) {
    if (!sendToClarity(entry)) pendingClarity.push(entry);
  }

  function report(event, formId, detail = {}, email = '') {
    if (event.endsWith('_error')) console.error('Shopify/Interspire signup diagnostic', { form: formId, ...detail });
    deliver({ event, formId, detail, email });
  }

  document.addEventListener('theme:analytics-loaded', () => pendingClarity.splice(0).forEach(deliver));

  // Shopify rejects customer forms without an hCaptcha token (HTTP 400), so the
  // opt-in is a normal navigation wired through Shopify's supported CAPTCHA API.
  function submitOptIn(form, email) {
    form.querySelector('[name="contact[email]"]').value = email;
    if (typeof window.Shopify?.captcha?.protect === 'function') {
      window.Shopify.captcha.protect(form, () => form.submit());
    } else {
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  function initializeOptIn(root) {
    const source = document.getElementById(root.dataset.sourceFormId);
    const emailInput = document.getElementById(root.dataset.emailInputId);
    const consent = document.getElementById(root.dataset.consentInputId);
    const optIn = root.querySelector('form');
    const status = root.querySelector('[data-interspire-status]');
    const retry = root.querySelector('[data-interspire-retry]');
    const retryEmail = retry?.querySelector('[type="email"]');
    const retryButton = retry?.querySelector('[type="submit"]');
    if (!source || !optIn || !status || !retry || !retryEmail || !retryButton) return;

    const key = `interspire-optin:${source.id}`;
    const attempt = storedAttempt(key);
    const sourceResult = source.querySelector('[data-interspire-source-result]')?.dataset.interspireSourceResult;
    const optInResult = optIn.querySelector('[data-interspire-optin-result]')?.dataset.interspireOptinResult;
    let started = null;
    let timer = 0;

    function show(message, allowRetry = false) {
      status.textContent = message;
      status.hidden = false;
      retry.hidden = !allowRetry;
    }

    function offerRetry(email, retrying) {
      retryEmail.value = email;
      retryButton.disabled = false;
      show(retrying ? root.dataset.retryFailedMessage : root.dataset.partialMessage, true);
    }

    function fail(email, retrying, type) {
      saveAttempt(key, { email, state: 'failed', at: Date.now() });
      offerRetry(email, retrying);
      report('interspire_optin_error', source.id, { outcome: retrying ? 'retry_failed' : 'partial', type }, email);
    }

    function start(email, retrying) {
      const current = { email, state: 'submitting', retrying, at: Date.now() };
      started = current;
      saveAttempt(key, current);
      retryButton.disabled = true;
      show(root.dataset.subscribingMessage);
      timer = window.setTimeout(() => {
        timer = 0;
        if (storedAttempt(key)?.at === current.at) fail(email, retrying, 'captcha_timeout');
      }, captchaWait);
      try {
        submitOptIn(optIn, email);
      } catch {
        window.clearTimeout(timer);
        timer = 0;
        fail(email, retrying, 'captcha_error');
      }
    }

    if (attempt?.state === 'submitting') {
      optInResultOnPage = true;
      if (optInResult === 'success') {
        clearAttempt(key);
        show(root.dataset.successMessage);
      } else {
        fail(attempt.email, attempt.retrying, optInResult === 'error' ? 'shopify_form' : 'unconfirmed');
      }
      // The opt-in form's URL fragment targets a hidden form, so the browser
      // does not scroll to the result on its own.
      status.scrollIntoView?.({ block: 'center' });
    } else if (attempt?.state === 'pending' && sourceResult === 'success') {
      start(attempt.email, false);
    } else if (attempt?.state === 'failed') {
      offerRetry(attempt.email, false);
    }

    // A page restored from the back/forward cache after the opt-in navigation
    // must not keep waiting for a submission that already left the page.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || !timer) return;
      window.clearTimeout(timer);
      timer = 0;
      const stored = storedAttempt(key);
      if (stored?.state === 'submitting' && stored.at === started?.at) {
        fail(stored.email, stored.retrying, 'unconfirmed');
      } else if (stored?.state === 'failed') {
        offerRetry(stored.email, false);
      } else {
        status.hidden = true;
        retry.hidden = true;
      }
    });

    // The primary form is never delayed: it keeps Shopify's own CAPTCHA
    // submission, and the opt-in runs only after Shopify confirms it.
    if (emailInput && consent) source.addEventListener('submit', () => {
      const email = emailInput.value.trim().toLowerCase();
      if (!consent.checked || !email || !source.checkValidity()) {
        clearAttempt(key);
        return;
      }
      saveAttempt(key, { email, state: 'pending', at: Date.now() });
    });

    retry.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!retry.checkValidity() || retryButton.disabled) return;
      start(retryEmail.value.trim().toLowerCase(), true);
    });
  }

  function initialize() {
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith('interspire-optin:')) {
          const value = storedAttempt(key);
          if (value) window.setTimeout(() => storedAttempt(key), Math.max(0, value.at + maxAge - Date.now()) + 1);
        }
      }
    } catch {}
    document.querySelectorAll('[data-interspire-optin]').forEach(initializeOptIn);
    // Shopify renders a customer form result on every customer form of the
    // page, so newsletter forms repeat the opt-in result already reported.
    if (optInResultOnPage) return;
    document.querySelectorAll('[data-interspire-newsletter-error]').forEach((marker) => {
      const form = marker.closest('form');
      report('interspire_newsletter_form_error', form?.id || 'newsletter', { outcome: 'shopify_form_error' }, form?.querySelector('[name="contact[email]"]')?.value || '');
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
